import get from 'lodash/get';
import { Stack } from 'react-bootstrap';

import FullLogoSvg from '@statics/images/full_logo.svg?react';

import type { PaymentDetails } from '../../store/actions';
import type { Requisition } from '../../types/requisition';
import { formatDate } from '../../utils/helpers';

const paymentStatusMap = {
  FREE: 'Free',
  SUCCEEDED: 'Complete',
  PENDING: 'Pending',
  CANCELED: 'Canceled',
};

type ReceiptTemplateProps = {
  paymentInfo: PaymentDetails;
  requisition: Requisition;
};

function ReceiptTemplate({ paymentInfo, requisition }: ReceiptTemplateProps) {
  const getUserName = () => {
    const nameObj = requisition.name || requisition.subject?.name || {};
    return nameObj.first + ' ' + nameObj.last;
  };

  const getPaymentStatus = () => {
    return paymentStatusMap[paymentInfo.payment_status as unknown as keyof typeof paymentStatusMap];
  };

  return (
    <div data-testid='receiptTemplateWrapper'>
      {!Object.keys(requisition).length ? (
        <p className='mb-0 text-center'>Generating Receipt...</p>
      ) : (
        <Stack gap={3}>
          <div className='d-flex align-items-center justify-content-center'>
            <FullLogoSvg desc='logo' width={'200px'} />
          </div>
          <div>
            <h4 className='h3 fw-bold text-center border-bottom pb-4 mb-4'>Thank you for your order</h4>
            <h5 className='fw-semibold'>Order Summary</h5>
            <Stack data-testid='orderSummarySection' gap={3} className='fs-sm pt-1 px-4'>
              <div className='d-flex justify-content-between'>
                <div>
                  <h6 className='fw-bold'>Requisition ID:</h6>
                  <p className='mb-0'>{requisition.requisition_id}</p>
                </div>
                <div>
                  <h6 className='fw-bold'>Order Date:</h6>
                  <p className='mb-0'>{formatDate(requisition.submitted_at)}</p>
                </div>
              </div>
              <div>
                <h6 className='fw-bold mb-2'>Order Details:</h6>
                <p className='fw-medium mb-0'>1 x MyOme Personal Genome</p>
                <ul className='text-secondary'>
                  <li>{get(requisition, 'orders[0].product_name', '')}</li>
                  <li>{get(requisition, 'orders[1].product_name', '')}</li>
                  <li>Clinician order review with DNAvisit</li>
                </ul>
              </div>
            </Stack>
            <h5 className='fw-semibold'>Shipping Details</h5>
            <div
              data-testid='shippingDetailsSection'
              className='d-flex justify-content-between fs-sm pt-1 px-4 pb-4 border-bottom'
            >
              <div>
                <h6 className='fw-bold'>Name:</h6>
                <p className='mb-0'>{getUserName()}</p>
              </div>
              <div>
                <h6 className='fw-bold'>Shipping Address</h6>
                <p className='mb-0 h-adr'>
                  <span className='d-block p-street-address'>
                    {get(requisition.subject, 'contact_info[0].address.street_address', '')}
                  </span>
                  <span className='d-block p-locality'>
                    {get(requisition.subject, 'contact_info[0].address.city', '')}
                  </span>
                  <span className='d-block'>
                    <span className='me-1 p-region'>
                      {get(requisition.subject, 'contact_info[0].address.region', '')},
                    </span>
                    <span className='p-postal-code'>
                      {get(requisition.subject, 'contact_info[0].address.postal_code', '')}
                    </span>
                  </span>
                </p>
              </div>
            </div>
            <div className='pt-4 px-4'>
              {paymentInfo.payment_status !== 'FREE' ? (
                <h6 className='fs-xs fw-bold mb-1'>Payment is {getPaymentStatus()}</h6>
              ) : null}
              <h6 className='fs-5 fw-bold'>Total: ${Number(paymentInfo.total_cost).toFixed(2)}</h6>
              {paymentInfo.payment_status === 'SUCCEEDED' && paymentInfo.charge_date ? (
                <p className='fs-sm fw-bold mb-0'>
                  Charge completed on: {new Date(paymentInfo.charge_date).toLocaleDateString()}
                </p>
              ) : null}
            </div>
          </div>
        </Stack>
      )}
    </div>
  );
}

export default ReceiptTemplate;
